import { analyticsService } from '../index-business';

export interface HomeworkEventData {
  attendanceId?: string;
  homeworkId?: string;
  studioClientId?: string;
  homeworks?: any[];
  submitData?: any;
  statusData?: any;
  [key: string]: any;
}

export class HomeworkTracker {
  static trackUpdateHomeworks(data: {
    attendanceId: string;
    homeworks: any[];
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      attendance_id: data.attendanceId,
      homeworks_count: data.homeworks?.length || 0,
      homework_types: data.homeworks?.map((hw) => hw.type).filter(Boolean) || [],
      homework_titles: data.homeworks?.map((hw) => hw.title).filter(Boolean) || [],
      has_descriptions: data.homeworks?.some((hw) => hw.description) || false,
      has_due_dates: data.homeworks?.some((hw) => hw.dueDate) || false,
      has_attachments: data.homeworks?.some((hw) => hw.attachments?.length > 0) || false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateHomeworks',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Class Homeworks Updated', eventData);
  }

  static trackUpsertStudioClientHomework(data: {
    studioClientId: string;
    homeworkId: string;
    submitData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_client_id: data.studioClientId,
      homework_id: data.homeworkId,
      has_text_answer: data.submitData?.textAnswer ? true : false,
      has_file_attachments: data.submitData?.fileAttachments?.length > 0 || false,
      attachments_count: data.submitData?.fileAttachments?.length || 0,
      answer_length: data.submitData?.textAnswer?.length || 0,
      submission_notes: data.submitData?.notes ? 'has_notes' : 'no_notes',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'upsertStudioClientHomework',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Student Homework Upserted', eventData);
  }

  static trackSubmitStudioClientHomework(data: {
    studioClientId: string;
    homeworkId: string;
    submitData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_client_id: data.studioClientId,
      homework_id: data.homeworkId,
      has_text_answer: data.submitData?.textAnswer ? true : false,
      has_file_attachments: data.submitData?.fileAttachments?.length > 0 || false,
      attachments_count: data.submitData?.fileAttachments?.length || 0,
      answer_length: data.submitData?.textAnswer?.length || 0,
      submission_notes: data.submitData?.notes ? 'has_notes' : 'no_notes',
      submitted_at: new Date().toISOString(),
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'submitStudioClientHomework',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Student Homework Submitted', eventData);
  }

  static trackUpdateStudioClientHomeworkStatus(data: {
    homeworkId: string;
    statusData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      homework_id: data.homeworkId,
      new_status: data.statusData?.status,
      teacher_feedback: data.statusData?.teacherFeedback ? 'has_feedback' : 'no_feedback',
      feedback_length: data.statusData?.teacherFeedback?.length || 0,
      grade: data.statusData?.grade,
      has_grade: data.statusData?.grade !== undefined && data.statusData?.grade !== null,
      reviewed_at: data.statusData?.reviewedAt || new Date().toISOString(),
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateStudioClientHomeworkStatus',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Student Homework Status Updated', eventData);
  }

  static trackUpdateHomework(data: {
    homeworkId: string;
    homeworkData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const updatedFields = data.homeworkData ? Object.keys(data.homeworkData) : [];

    const eventData = {
      homework_id: data.homeworkId,
      updated_fields: updatedFields,
      homework_title: data.homeworkData?.title,
      homework_type: data.homeworkData?.type,
      homework_description: data.homeworkData?.description ? 'has_description' : 'no_description',
      description_length: data.homeworkData?.description?.length || 0,
      has_due_date: data.homeworkData?.dueDate ? true : false,
      due_date: data.homeworkData?.dueDate,
      has_attachments: data.homeworkData?.attachments?.length > 0 || false,
      attachments_count: data.homeworkData?.attachments?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateHomework',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Homework Updated', eventData);
  }

  static trackHomeworkApiCall(
    endpoint: string,
    data: HomeworkEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Homework API: ${endpoint}`, eventData);
  }
}

export const trackHomeworkSuccess = (endpoint: string, data: HomeworkEventData, duration?: number) => {
  HomeworkTracker.trackHomeworkApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackHomeworkError = (endpoint: string, error: string, data: HomeworkEventData, duration?: number) => {
  HomeworkTracker.trackHomeworkApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default HomeworkTracker;
